import React from 'react';
import apiSlice from './apiSlice';

export interface User {
  id: number;
  firstname: string;
  lastname: string;
  username: string | null;
  profileImage: string | null;
  team: string | null;
  instaLink: string | null;
  twitterLink: string | null;
  linkedinLink: string | null;
  youtubeLink: string | null;
  status: string;
}

export interface Contestant {
  id: number;
  roundId: number;
  userId: number;
  user: User;
}

export interface Round {
  id: number;
  votingSessionId: number;
  roundNumber: number;
  title: string;
  description: string;
  image: string | null;
  numberOfContestants: number;
  status: boolean;
  contestants: Contestant[];
  contestantCount: number;
  isActive: boolean;
  isPast: boolean;
}

export interface RemainingTime {
  totalSeconds: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export interface VotingSession {
  id: number;
  title: string;
  description: string;
  banner: string | null;
  createdRound: number;
  startTime: string;
  endTime: string;
  status: string;
}

export interface VotingSessionsResponse {
  data: VotingSession[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}
export interface ActiveContestant {
  id: number;
  user: {
    firstname: string;
    lastname: string;
    team: string;
    profileImage: string;
  };
}

export interface Userpower {
  powerPerRound: string;
  role: string;
  totalPower?: number;
  usedPower?: number;
  remainingPower?: number;
}

export interface VoteItem {
  contestantUserId: number;
  votePoints: number;
}

export interface VoteRequest {
  SessionId: number;
  pollId: number;
}
export const votingApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getVotingRounds: builder.query<VotingSessionsResponse, { skip?: number; take?: number } | void>(
      {
        query: (params) => {
          const skip = params?.skip ?? 0;
          const take = params?.take ?? 200;

          const searchParams = new URLSearchParams({
            skip: String(skip),
            take: String(take),
          });

          return `/voting-sessions?${searchParams.toString()}`;
        },
        providesTags: ['Voting'],
      }
    ),
    getVotingContestent: builder.query<VotingSession, number>({
      query: (id) => `/voting/session/${id}/with-rounds`,
      providesTags: ['Voting'],
    }),
    getPower: builder.query<Userpower, number | void>({
      query: (roundId) => (roundId ? `/voting/me/power?roundId=${roundId}` : '/voting/me/power'),
      providesTags: ['Voting'],
    }),
    voting: builder.mutation<
      any,
      {
        sessionId: number;
        pollId: number;
        body: {
          distributions: {
            pollContestantId: number;
            votePower: number;
          }[];
        };
      }
    >({
      query: ({ sessionId, pollId, body }) => ({
        url: `/voting-sessions/${sessionId}/polls/${pollId}/vote`,
        method: 'POST',
        body,
      }),
    }),
    getVotingResult: builder.query<any, number>({
      query: (id) => `/voting/results/round/${id}`,
      providesTags: ['Voting'],
    }),
    getVotingPoll: builder.query<any, number>({
      query: (id) => `/voting-sessions/${id}`,
      providesTags: ['Voting'],
    }),
    getVoting: builder.query<any, { sessionId: number; pollId: number }>({
      query: ({ sessionId, pollId }) => ({
        url: `/voting-sessions/${sessionId}/polls/${pollId}/vote`,
        method: 'GET',
      }),
      providesTags: ['Voting'],
    }),
  }),
});

export const {
  useGetVotingRoundsQuery,
  useGetVotingContestentQuery,
  useGetPowerQuery,
  useGetVotingResultQuery,
  useVotingMutation,
  useGetVotingPollQuery,
  useGetVotingQuery,
} = votingApi;
